import React, { useState, useCallback } from "react";
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Header from "src/components/wizard/Header";
import OnsiteInventorySearch from "./OnsiteInventorySearch";
import { fullWidthDialog } from "src/constants/dialog";
import useMutation from "src/hooks/useMutation";
import useQueryParams from "src/hooks/useQueryParams";
import { ADD_SHIPMENT_ITEMS_MUTATION } from "src/constants/graphql/mutations";
import { GET_SHIPMENT_QUERY } from "src/constants/graphql/queries";

export default function OnsiteInventoryToWarehouseStorageModal({ project, onClose }) {
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [adding, setAdding] = useState(false);
  const params = useQueryParams();
  const shipmentId = params.get("id");

  const [addShipmentItem] = useMutation(ADD_SHIPMENT_ITEMS_MUTATION);

  const handleToggleItem = useCallback(assetId => {
    setSelectedItems(prev => {
      const newSelected = new Set(prev);
      if (newSelected.has(assetId)) {
        newSelected.delete(assetId);
      } else {
        newSelected.add(assetId);
      }
      return newSelected;
    });
  }, []);

  const handleSelectAll = useCallback((assetIds, shouldSelect) => {
    setSelectedItems(prev => {
      const newSelected = new Set(prev);
      assetIds.forEach(id => {
        if (shouldSelect) {
          newSelected.add(id);
        } else {
          newSelected.delete(id);
        }
      });
      return newSelected;
    });
  }, []);

  const handleBulkAdd = async () => {
    if (selectedItems.size === 0) return;
    setAdding(true);
    const itemIds = Array.from(selectedItems);

    try {
      const promises = itemIds.map((assetId, index) => {
        const isLast = index === itemIds.length - 1;
        const mutationOptions = {
          variables: {
            assetId,
            shipmentId,
            quantity: 0,
          },
        };

        if (isLast) {
          mutationOptions.refetchQueries = [
            { query: GET_SHIPMENT_QUERY, variables: { id: shipmentId } },
          ];
          mutationOptions.awaitRefetchQueries = true;
        }

        return addShipmentItem(mutationOptions);
      });

      await Promise.all(promises);
      onClose();
    } catch (error) {
      console.error("Error adding items:", error);
    } finally {
      setAdding(false);
    }
  };

  return (
    <Dialog open fullWidth maxWidth="sm" onClose={onClose} sx={fullWidthDialog}>
      <Box bgcolor="#f4f6f8">
        <Header title="Onsite Captured Inventory" />
      </Box>
      <DialogContent
        sx={{
          background: "#f4f6f8",
          px: { xs: 0, sm: 3 },
          pb: 1.5,
        }}
      >
        <Box display="flex" justifyContent="center" px={{ xs: 2, sm: 0 }}>
          <Box
            mx={2}
            component="img"
            borderRadius={3}
            width="100%"
            maxWidth={300}
            minHeight="100px"
            src="/item-lookup.png"
          />
        </Box>
        <Typography fontWeight={500} align="center" fontSize={18} px={{ xs: 2, sm: 0 }}>
          Select inventory items captured onsite to receive into the warehouse
        </Typography>
        <Box mt={2}>
          <OnsiteInventorySearch
            selectedItems={selectedItems}
            onToggleItem={handleToggleItem}
            onSelectAll={handleSelectAll}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: { xs: 2, sm: 3 }, pb: 2, background: "#f4f6f8" }}>
        <Button
          variant="contained"
          onClick={handleBulkAdd}
          disabled={adding || selectedItems.size === 0}
          sx={{ textTransform: "none" }}
          startIcon={adding ? <CircularProgress size={20} color="inherit" /> : null}
        >
          Add {selectedItems.size || ""} {selectedItems.size === 1 ? "Item" : "Items"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
